import React from 'react';
import Link from 'next/link';

const TOKEN_RE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(([^)\s]+)\)|\*[^*]+\*)/g;

function isSafeHref(href: string) {
  return /^(https?:\/\/|mailto:|\/)/i.test(href);
}

export function renderInlineMarkdown(text: string): React.ReactNode[] {
  const source = String(text || '');
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(source)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(source.slice(lastIndex, match.index));
    }

    const token = match[0];
    const key = `${match.index}-${token}`;
    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(
        <code key={key} className="rounded bg-white/8 px-1 py-0.5 font-['DM_Mono'] text-[0.9em] text-[var(--c-text-primary)]">
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      const label = linkMatch?.[1] || token;
      const href = linkMatch?.[2] || '';
      if (href && isSafeHref(href)) {
        const external = /^https?:\/\//i.test(href);
        nodes.push(
          external ? (
            <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="text-[var(--c-accent)] underline-offset-4 hover:underline">
              {label}
            </a>
          ) : (
            <Link key={key} href={href} className="text-[var(--c-accent)] underline-offset-4 hover:underline">
              {label}
            </Link>
          )
        );
      } else {
        nodes.push(label);
      }
    } else if (token.startsWith('*') && token.endsWith('*')) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    } else {
      nodes.push(token);
    }

    lastIndex = match.index + token.length;
  }

  if (lastIndex < source.length) {
    nodes.push(source.slice(lastIndex));
  }

  return nodes;
}
